import * as yup from 'yup'
import {
  CourseLevel,
  // CourseVisibility,
  DiscountTypeOptions,
} from '../../core/common/data/json/courses'
import { getCategories } from '../../api/courses'
import { benefitsIcons } from '../../core/common/data/json/benefitsIcon'

// --- Step 1: Course Information ---
export const courseInformationFieldsConfig = {
  title: {
    label: 'Course Title',
    type: 'text',
    required: true,
    placeholder: 'Enter course title',
    schema: yup.string().required('Course Title is required'),
  },
  category_id: {
    label: 'Course Category',
    type: 'select',
    required: true,
    placeholder: 'Select category',
    options: [],
    isSearchableAndInfinite: true,
    queryKeyBase: 'categories',
    apiFn: (params: any) => getCategories({ ...params, type: 'course' }),
    schema: yup.string().required('Course Category is required'),
  },
  course_level: {
    label: 'Course Level',
    type: 'select',
    required: true,
    placeholder: 'Select level',
    options: CourseLevel,
    schema: yup.string().required('Course Level is required'),
  },
  max_student: {
    label: 'Max Number of Students',
    type: 'number',
    required: true,
    placeholder: 'e.g., 50',
    schema: yup
      .number()
      .required('Max Number of Students is required')
      .positive('Must be a positive number')
      .integer('Must be an integer')
      .typeError('Must be a number'),
  },
  // is_public: {
  //   label: 'Public / Private Course',
  //   type: 'select',
  //   required: false,
  //   placeholder: 'Select visibility',
  //   options: CourseVisibility,
  //   schema: yup
  //     .number()
  //     .oneOf([0, 1], 'Invalid visibility option')
  //     .required('Course Visibility is required'),
  // },
  short_description: {
    label: 'Short Description',
    type: 'text',
    required: true,
    placeholder: 'Briefly describe the course',
    schema: yup
      .string()
      .required('Short Description is required')
      .max(255, 'Short Description must not exceed 255 characters'),
  },
  description: {
    label: 'Course Description',
    type: 'richtext',
    required: true,
    placeholder: 'Provide a detailed description of the course',
    schema: yup.string().required('Course Description is required'),
  },
  studied: {
    label: 'Apa yang akan dipelajari',
    type: 'array',
    required: true,
    placeholder: 'Apa yang akan dipelajari',
    schema: yup.array(
      yup.string().required('Apa yang akan dipelajari is required'),
    ),
  },
  thumbnail: {
    label: 'Course Thumbnail',
    type: 'file',
    required: true,
    placeholder: 'Upload image',
    schema: yup
      .mixed()
      .test('fileRequired', 'Course Thumbnail is required', (value: any) => {
        if (Array.isArray(value)) return value.length > 0
        return !!value
      })
      .test(
        'fileType',
        'Unsupported File Format (only JPG, JPEG, PNG)',
        (value: any) => {
          const file = value?.[0]
          if (!file && typeof file !== 'string') {
            return true
          }

          if (typeof file === 'string')
            return ['jpeg', 'png', 'jpg'].includes(value.split('.')[1])
          return ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)
        },
      )
      .test('fileSize', 'File too large (max 5MB)', (value: any) => {
        const file = value?.[0]
        if (!file && typeof file !== 'string') return true
        if (typeof file === 'string') return true

        return file.size <= 5 * 1024 * 1024
      }),
  },
}

// --- Step 2: Content ---
export const contentFieldsConfig = {
  topic: {
    label: 'Course Topics & Lessons',
    placeholder: 'Course Topics & Lessons',
    type: 'hidden',
    required: true,
    schema: yup
      .array(
        yup.object().shape({
          name: yup.string().required('Topic name is required'),
          topic_id: yup.number().nullable().optional(), // Backend ID for existing topics
          lesson: yup
            .array(
              yup.object().shape({
                name: yup.string().required('Lesson name is required'),
                video_link: yup
                  .string()
                  .url('Must be a valid URL')
                  .required('Video URL is required'),
                description: yup.string(),
                is_premium: yup.boolean(),
                lesson_id: yup.number().nullable().optional(), // Backend ID for existing lessons
              }),
            )
            .min(1, 'At least one lesson is required for each topic')
            .required('Lessons are required'),
        }),
      )
      .min(1, 'At least one topic is required')
      .required('Course content is required'),
  },
}

// --- Step 3: Additional Information ---
export const additionalInformationFieldsConfig = {
  link_group: {
    label: 'Link Group',
    type: 'url',
    required: true,
    placeholder: 'e.g., https://t.me/yourgroup',
    schema: yup
      .string()
      .url('Must be a valid URL')
      .required('Link Group is required'),
  },
  link_ebook: {
    label: 'Link Ebook',
    type: 'url',
    required: true,
    placeholder: 'e.g., https://example.com/ebook.pdf',
    schema: yup
      .string()
      .url('Must be a valid URL')
      .required('Link Ebook is required'),
  },
  slug: {
    label: 'Slugs / Keywords',
    type: 'text',
    required: true,
    placeholder: 'Slug',
    schema: yup.string().nullable(),
  },
  benefits: {
    label: 'Benefits',
    type: 'array',
    required: false,
    options: benefitsIcons,
    schema: yup
      .array(
        yup.object().shape({
          icon: yup.string().required('Icon is required'),
          name: yup.string().required('Benefit is required'),
        }),
      )
      .nullable()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string') {
          try {
            const parsed = JSON.parse(originalValue)
            return Array.isArray(parsed) ? parsed : null
          } catch {
            return null
          }
        }
        return value
      }),
  },
}

// --- Step 4: Pricing ---
export const pricingFieldsConfig = {
  price: {
    label: 'Course Price',
    type: 'number',
    required: true,
    placeholder: 'e.g., 250000 or 0 for free',
    schema: yup
      .number()
      .required('Course Price is required')
      .min(0, 'Price cannot be negative')
      .typeError('Must be a number'),
  },
  discount_type: {
    label: 'Discount Type',
    type: 'select',
    required: true,
    placeholder: 'Discount',
    options: DiscountTypeOptions,
    schema: yup.string().required('Discount type is required'),
  },
  discount: {
    label: 'Discount Value (Optional)',
    type: 'number',
    required: false,
    placeholder: 'e.g., 50 (for percentage) or 50000 (for amount)',
    schema: yup
      .number()
      .typeError('Masukan 0 jika tidak ingin ada diskon')
      .nullable()
      // .when('discount_type', {
      //   is: (val: string) => val !== 'NONE',
      //   then: (schema) => schema.required("Discount value is required when discount type is not 'None'").positive("Discount value must be positive").typeError("Must be a number"),
      //   otherwise: (schema) => schema.nullable(),
      // })
      // Add validation for percentage vs amount if needed, e.g., percentage <= 100
      .test(
        'discount-percentage-max',
        'Percentage cannot exceed 100%',
        function (value) {
          const { discount_type } = this.parent
          if (
            discount_type === 'PERCENTAGE' &&
            typeof value === 'number' &&
            value > 100
          ) {
            return this.createError({
              message: 'Percentage cannot exceed 100%',
            })
          }
          return true
        },
      ),
  },
  cross_sell: {
    label: 'List Product Cross Sell',
    type: 'array',
    placeholder: 'Cross Sell',
    required: false,
    options: [],
    schema: yup
      .array(
        yup.object().shape({
          cross_course_id: yup
            .number()
            .required('Cross-sell course ID is required'),
          note: yup.string().nullable(),
          price: yup
            .number()
            .required('Price is required')
            .typeError('Price must be a number'),
        }),
      )
      .nullable()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string') {
          try {
            const parsed = JSON.parse(originalValue)
            return Array.isArray(parsed) ? parsed : null
          } catch {
            return null
          }
        }
        return value
      }),
  },
}

export const courseBasicInformationSchema = yup
  .object()
  .shape(
    Object.fromEntries(
      Object.entries(courseInformationFieldsConfig).map(([key, val]) => [
        key,
        val.schema,
      ]),
    ),
  )

export const courseContentSchema = yup
  .object()
  .shape(
    Object.fromEntries(
      Object.entries(contentFieldsConfig).map(([key, val]) => [
        key,
        val.schema,
      ]),
    ),
  )

export const courseAdditionalInformationSchema = yup
  .object()
  .shape(
    Object.fromEntries(
      Object.entries(additionalInformationFieldsConfig).map(([key, val]) => [
        key,
        val.schema,
      ]),
    ),
  )

export const coursePricingSchema = yup
  .object()
  .shape(
    Object.fromEntries(
      Object.entries(pricingFieldsConfig).map(([key, val]) => [
        key,
        val.schema,
      ]),
    ),
  )

export const fullCourseCreateSchema = yup.object().shape({
  ...courseBasicInformationSchema.fields,
  ...courseContentSchema.fields,
  ...courseAdditionalInformationSchema.fields,
  ...coursePricingSchema.fields,
})

export type CourseBasicInformationFormValues = yup.InferType<
  typeof courseBasicInformationSchema
>
export type CourseContentFormValues = yup.InferType<typeof courseContentSchema>
export type CourseAdditionalInformationFormValues = yup.InferType<
  typeof courseAdditionalInformationSchema
>
export type CoursePricingFormValues = yup.InferType<typeof coursePricingSchema>

export type CourseCreateFormValues = CourseBasicInformationFormValues &
  CourseContentFormValues &
  CourseAdditionalInformationFormValues &
  CoursePricingFormValues

export function getInitialValuesFromConfig(
  config: Record<string, any>,
): Record<string, any> {
  return Object.keys(config).reduce((acc, key) => {
    const field = config[key]
    if (field.type === 'checkbox') {
      acc[key] = false
    } else if (field.type === 'file') {
      acc[key] = null
    } else if (field.type === 'array' || field.type === 'chips') {
      acc[key] = []
    } else if (field.type === 'number') {
      acc[key] = ''
    } else if (field.type === 'richtext') {
      acc[key] = ''
    } else if (field.type === 'radio') {
      acc[key] = field.options?.[0]?.value || ''
    } else {
      acc[key] = null
    }
    return acc
  }, {} as Record<string, any>)
}

// Initial values for each step
export const initialValuesStep1 = getInitialValuesFromConfig(
  courseInformationFieldsConfig,
)
export const initialValuesStep2 = getInitialValuesFromConfig(
  contentFieldsConfig,
)
export const initialValuesStep3 = getInitialValuesFromConfig(
  additionalInformationFieldsConfig,
)
export const initialValuesStep4 = getInitialValuesFromConfig(
  pricingFieldsConfig,
)

export const initialFullFormValues: CourseCreateFormValues = {
  ...initialValuesStep1,
  ...initialValuesStep2,
  ...initialValuesStep3,
  ...initialValuesStep4,
}

// --- Function to transform Formik values into the desired API payload structure ---
export function transformCoursePayload(
  values: any,
  originalValues?: any,
): FormData {
  const formData = new FormData()

  // Step 1 Fields
  formData.append('title', values.title)
  formData.append('category_id', values.category_id)
  formData.append('course_level', values.course_level)
  formData.append('max_student', values.max_student)
  // formData.append('is_public', values.is_public) // Convert boolean/number to string '0' or '1'
  formData.append('short_description', values.short_description)
  formData.append('description', values.description)

  formData.append('studied', JSON.stringify(values.studied || []))

  // Handle thumbnail: user can only upload new file or keep existing
  if (typeof values?.thumbnail !== 'string') {
    // User uploaded new file
    formData.append('thumbnail', values.thumbnail[0])
  } else if (!originalValues) {
    // Create mode - only append if there's a value (shouldn't happen with file input, but for safety)
    if (values?.thumbnail) {
      formData.append('thumbnail', values.thumbnail)
    }
    // If no file in create mode, don't append anything
  }
  // Edit mode - if no changes made, don't append anything (backend will ignore this field)

  // Step 2 Fields (Topic) - Transform back to backend format with proper IDs
  const transformedTopics = (values.topic || []).map((topic: any) => ({
    ...topic,
    id: topic.topic_id, // Convert topic_id back to id for backend
    lesson: (topic.lesson || []).map((lesson: any) => ({
      ...lesson,
      id: lesson.lesson_id, // Convert lesson_id back to id for backend
    })),
  }))
  formData.append('topic', JSON.stringify(transformedTopics))

  // Step 3 Fields
  formData.append('link_ebook', values.link_ebook)
  formData.append('link_group', values.link_group)
  // 'slug' from Formik is an array of strings, stringify it
  formData.append('slug', values.slug)
  // 'cross_sell' from Formik is an array of objects, stringify it
  formData.append('cross_sell', JSON.stringify(values.cross_sell || []))
  // 'benefits' from Formik is an array of objects, stringify it
  formData.append('benefits', JSON.stringify(values.benefits || []))

  // Step 4 Fields
  formData.append('price', values.price.toString())
  formData.append('discount_type', values.discount_type)
  // Only append discount if it has a value (not null/undefined)
  if (
    values.discount !== null &&
    values.discount !== undefined &&
    values.discount !== ''
  ) {
    formData.append('discount', values.discount.toString())
  }

  formData.append('type', 'Course')

  return formData
}
